import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { TemplateSelection } from "@/components/check-ins/TemplateSelection";
import { CheckInProgressBar } from "@/components/check-ins/CheckInProgressBar";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import SlackLogo from '@/assets/slack-logo.png';
import { Mail } from 'lucide-react';
import { CheckInStep, LEGACY_STEP_MAPPING, getStepNumber } from "@/constants/checkInSteps";
import { slackApi } from "@/api/slack";
import { startOfToday, addDays, setHours, setMinutes, getDay, addMonths, setDate } from "date-fns";
import { toZonedTime } from 'date-fns-tz';
import { questionService } from "@/services/questionService";
import { mapReportConfigQuestionIds, createDefaultReportConfig } from "@/utils/reportConfig";

type FormData = {
  name: string;
};

type CommunicationChannel = 'slack' | 'email';

// Update FormStep type to use the enum
type FormStep = CheckInStep;

interface Template {
  template_id: string;
  name: string;
  description: string;
  icon: string;
  background_class: string;
  // Schedule settings
  default_cadence: 'daily' | 'weekly' | 'bi-weekly' | 'monthly' | 'one-off' | 'on-demand';
  default_days_of_week: string[] | null;
  default_scheduled_day: string | null;
  default_reminder_time: string; // "HH:mm:ss"
  default_cutoff_time: string;  // "HH:mm:ss"
  default_timezone_reminder: boolean;
  default_min_reminder_offset_hours: number; // New field for minimum reminder offset hours
  default_voice_settings: any; // Added field for voice settings
  report_config?: any;
}

// Map day names to numbers (0 = Sunday, 1 = Monday, etc.)
const dayNameToNumber: Record<string, number> = {
  'Sunday': 0,
  'Monday': 1,
  'Tuesday': 2,
  'Wednesday': 3,
  'Thursday': 4,
  'Friday': 5,
  'Saturday': 6
};

function calculateNextReportDateTime(
  cadence: Template['default_cadence'],
  daysOfWeek: string[] | null,
  scheduledDay: string | null,
  cutoffTime: string,
  timezone: string
): string | null {
  // Parse the cutoff time
  const [hours, minutes] = cutoffTime.split(':').map(Number);
  let baseDate = startOfToday();
  baseDate = setHours(setMinutes(baseDate, minutes), hours);

  switch (cadence) {
    case 'daily': {
      if (!daysOfWeek?.length) return null;
      
      // Find the next day that's in the daysOfWeek array
      const today = getDay(baseDate); // 0-6
      const nextDays = daysOfWeek
        .map(day => dayNameToNumber[day])
        .filter(day => day >= today)
        .sort();

      if (nextDays.length > 0) {
        // There's a day this week
        return toZonedTime(addDays(baseDate, nextDays[0] - today), timezone).toISOString();
      } else {
        // Need to wait until next week
        const firstDayNextWeek = Math.min(...daysOfWeek.map(day => dayNameToNumber[day]));
        return toZonedTime(addDays(baseDate, 7 - today + firstDayNextWeek), timezone).toISOString();
      }
    }

    case 'weekly': {
      if (!daysOfWeek?.length) return null;
      
      // Same logic as daily
      const today = getDay(baseDate);
      const nextDays = daysOfWeek
        .map(day => dayNameToNumber[day])
        .filter(day => day >= today)
        .sort();

      if (nextDays.length > 0) {
        return toZonedTime(addDays(baseDate, nextDays[0] - today), timezone).toISOString();
      } else {
        const firstDayNextWeek = Math.min(...daysOfWeek.map(day => dayNameToNumber[day]));
        return toZonedTime(addDays(baseDate, 7 - today + firstDayNextWeek), timezone).toISOString();
      }
    }

    case 'bi-weekly': {
      if (!daysOfWeek?.length) return null;
      
      // Similar to weekly but with bi-weekly cadence
      const today = getDay(baseDate);
      const nextDays = daysOfWeek
        .map(day => dayNameToNumber[day])
        .filter(day => day >= today)
        .sort();

      if (nextDays.length > 0) {
        // First occurrence is like weekly
        return toZonedTime(addDays(baseDate, nextDays[0] - today), timezone).toISOString();
      } else {
        const firstDayNextWeek = Math.min(...daysOfWeek.map(day => dayNameToNumber[day]));
        return toZonedTime(addDays(baseDate, 7 - today + firstDayNextWeek), timezone).toISOString();
      }
    }

    case 'monthly': {
      if (!scheduledDay) return null;
      
      const targetDay = parseInt(scheduledDay, 10);
      let nextDate = setDate(baseDate, targetDay);
      
      // If the target day has already passed this month, move to next month
      if (nextDate < baseDate) {
        nextDate = setDate(addMonths(baseDate, 1), targetDay);
      }
      
      return toZonedTime(nextDate, timezone).toISOString();
    }

    case 'one-off':
    case 'on-demand':
      return null;

    default:
      return null;
  }
}

export function NewCheckIn() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [currentStep, setCurrentStep] = useState<FormStep>(CheckInStep.TEMPLATE_NAME);
  const [formData, setFormData] = useState<FormData>({ name: "" });
  const [selectedTemplate, setSelectedTemplate] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [checkInId, setCheckInId] = useState<string | null>(null);
  const [templateDetails, setTemplateDetails] = useState<Template | null>(null);
  const [draftLoaded, setDraftLoaded] = useState(false);
  const [communicationChannel, setCommunicationChannel] = useState<CommunicationChannel>('email');
  const [slackWorkspaces, setSlackWorkspaces] = useState<{ workspace_id: string; team_name: string }[]>([]);
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState<string | null>(null);
  const [isLoadingWorkspaces, setIsLoadingWorkspaces] = useState(false);

  // Check for step parameter in URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const stepParam = params.get('step');
    const slackConnected = params.get('slack_connected') === 'true';
    const slackError = params.get('slack_error');
    
    // Handle Slack OAuth errors
    if (slackError) {
      console.error('Slack OAuth error:', slackError, 'Detail:', params.get('detail'));
      toast.error(`Slack connection failed: ${slackError}. Please try again.`);
      setCurrentStep(CheckInStep.CHANNEL);
      return;
    }
    
    if (slackConnected) {
      // Check if we have necessary data in localStorage to restore the flow
      const storedFormData = localStorage.getItem('check_in_form_data');
      const storedCheckInId = localStorage.getItem('check_in_id');
      const storedTemplateId = localStorage.getItem('check_in_template_id');
      // Get communication channel but set it to slack regardless since we're coming from Slack OAuth
      localStorage.getItem('check_in_communication_channel'); // Just to clear the item
      const storedStep = localStorage.getItem('check_in_step');
      
      console.log('Slack connected, stored data:', {
        hasFormData: !!storedFormData,
        hasCheckInId: !!storedCheckInId,
        hasTemplateId: !!storedTemplateId,
        storedStep
      });
      
      if (storedFormData && storedCheckInId && storedTemplateId) {
        // Restore the stored data
        setFormData(JSON.parse(storedFormData));
        setCheckInId(storedCheckInId);
        setSelectedTemplate(storedTemplateId);
        
        // Set the communication channel to Slack
        setCommunicationChannel('slack');
        
        // Set the current step based on stored step or default to CHANNEL
        setCurrentStep(storedStep ? storedStep as FormStep : CheckInStep.CHANNEL);
        
        // Fetch template details
        const fetchTemplateDetails = async () => {
          try {
            const { data, error } = await supabase
              .from('templates')
              .select(`
                template_id,
                name,
                description,
                icon,
                background_class,
                default_cadence,
                default_days_of_week,
                default_scheduled_day,
                default_reminder_time,
                default_cutoff_time,
                default_timezone_reminder,
                default_min_reminder_offset_hours,
                default_voice_settings,
                report_config
              `)
              .eq('template_id', storedTemplateId)
              .single();
    
            if (error) throw error;
            setTemplateDetails(data);
          } catch (err) {
            console.error('Error fetching template details:', err);
          }
        };
        
        fetchTemplateDetails();
      } else {
        // If we don't have necessary data, show an error
        toast.error("Session data not found. Please start the check-in creation process again.");
        setCurrentStep(CheckInStep.TEMPLATE_NAME);
      }
    } else if (stepParam === 'channel') {
      // If explicitly directed to the channel step
      setCurrentStep(CheckInStep.CHANNEL);
    }
  }, [location]);

  // Fetch template details when selected
  useEffect(() => {
    const fetchTemplateDetails = async () => {
      if (!selectedTemplate) return;

      try {
        const { data, error } = await supabase
          .from('templates')
          .select(`
            template_id,
            name,
            description,
            icon,
            background_class,
            default_cadence,
            default_days_of_week,
            default_scheduled_day,
            default_reminder_time,
            default_cutoff_time,
            default_timezone_reminder,
            default_min_reminder_offset_hours,
            default_voice_settings,
            report_config
          `)
          .eq('template_id', selectedTemplate)
          .single();

        if (error) throw error;
        setTemplateDetails(data);
      } catch (err) {
        console.error('Error fetching template details:', err);
        toast.error('Failed to load template details');
      }
    };

    fetchTemplateDetails();
  }, [selectedTemplate]);

  // Load draft on mount
  useEffect(() => {
    if (!user?.id) return;
    let isMounted = true;
    const loadDraft = async () => {
      setIsSubmitting(true);
      try {
        const { data: draft, error } = await supabase
          .from("check_ins")
          .select("*")
          .eq("created_by_user_id", user.id)
          .eq("first_setup_completed", false)
          .order("updated_at", { ascending: false })
          .limit(1)
          .maybeSingle();
        if (error) throw error;
        if (draft) {
          setCheckInId(draft.check_in_id);
          setFormData({ name: draft.name || "" });
          setSelectedTemplate(draft.template_id || "");
          
          // Handle legacy step names and convert to new step format
          if (draft.last_completed_step) {
            const normalizedStep = LEGACY_STEP_MAPPING[draft.last_completed_step as keyof typeof LEGACY_STEP_MAPPING] 
              || draft.last_completed_step;
            setCurrentStep(normalizedStep as FormStep);
          } else {
            setCurrentStep(CheckInStep.TEMPLATE_NAME);
          }
        }
      } catch (err) {
        toast.error("Failed to load draft check-in");
      } finally {
        if (isMounted) setDraftLoaded(true);
        setIsSubmitting(false);
      }
    };
    loadDraft();
    return () => { isMounted = false; };
  }, [user?.id]);

  // Fetch Slack workspaces when Slack is selected
  useEffect(() => {
    if (communicationChannel !== 'slack') return;
    
    (async () => {
      setIsLoadingWorkspaces(true);
      try {
        const { connected, workspace, error } = await slackApi.getWorkspaceInfo();
        
        if (connected && workspace) {
          setSlackWorkspaces([workspace]);
          setSelectedWorkspaceId(workspace.workspace_id);
        } else {
          setSlackWorkspaces([]);
          setSelectedWorkspaceId(null);
          if (error) {
            console.error('Slack workspace error:', error);
          }
        }
      } catch (error) {
        console.error('Error fetching workspace info:', error);
        setSlackWorkspaces([]);
        setSelectedWorkspaceId(null);
      } finally {
        setIsLoadingWorkspaces(false);
      }
    })();
  }, [communicationChannel]);

  // Update getCurrentStepNumber
  const getCurrentStepNumber = () => {
    return getStepNumber(currentStep);
  };

  const handleCancel = () => {
    navigate("/check-ins");
  };

  const handleBack = () => {
    if (currentStep === CheckInStep.CHANNEL) {
      setCurrentStep(CheckInStep.TEMPLATE_NAME);
    }
  };

  const handleChannelSelect = async () => {
    if (!checkInId) {
      toast.error('No check-in ID found');
      return;
    }

    // If Slack is selected but no workspace is connected, show error
    if (communicationChannel === 'slack' && !slackWorkspaces.length) {
      toast.error('Please connect your Slack workspace first');
      return;
    }

    setIsSubmitting(true);
    try {
      const updateData: Record<string, any> = {
        communication_channel: communicationChannel,
        slack_workspace_id: communicationChannel === 'slack' ? selectedWorkspaceId : null,
        last_completed_step: CheckInStep.CHANNEL,
        first_setup_completed: true // Mark as completed since we're removing the employee step
      };
      
      const { error } = await supabase
        .from('check_ins')
        .update(updateData)
        .eq('check_in_id', checkInId);
      if (error) throw error;
      
      // Clear stored data in localStorage
      localStorage.removeItem('check_in_form_data');
      localStorage.removeItem('check_in_id');
      localStorage.removeItem('check_in_template_id');
      localStorage.removeItem('check_in_communication_channel');
      localStorage.removeItem('check_in_step');
      
      toast.success('Check-in created successfully! Note: The check-in is currently paused. You can activate it from the check-in details page.');
      
      // Get the slug to navigate to the check-in details page
      const { data: checkIn, error: slugError } = await supabase
        .from('check_ins')
        .select('slug')
        .eq('check_in_id', checkInId)
        .single();
      
      if (slugError) throw slugError;
      
      // Navigate to the check-in details page
      navigate(`/check-ins/${checkIn.slug}`);
    } catch (error: any) {
      toast.error(error.message || 'Failed to save communication channel');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDiscardDraft = async () => {
    if (!checkInId) return;
    setIsSubmitting(true);
    try {
      const { error } = await supabase
        .from("check_ins")
        .delete()
        .eq("check_in_id", checkInId);
      if (error) throw error;

      // Clear all check-in related localStorage items
      localStorage.removeItem('check_in_form_data');
      localStorage.removeItem('check_in_id');
      localStorage.removeItem('check_in_template_id');
      localStorage.removeItem('check_in_communication_channel');
      localStorage.removeItem('check_in_step');

      setCheckInId(null);
      setFormData({ name: "" });
      setSelectedTemplate("");
      setCurrentStep(CheckInStep.TEMPLATE_NAME);
      toast.success("Draft discarded.");
      
      // Navigate back to check-ins list
      navigate('/check-ins');
    } catch (error: any) {
      toast.error(error.message || "Failed to discard draft");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Function to generate a slug from the check-in name
  const generateSlug = (name: string): string => {
    if (!name || name.trim().length === 0) {
      return 'check-in'; // Default fallback for empty names
    }
    
    // Convert to lowercase
    // Replace spaces with hyphens
    // Remove special characters
    // Keep numbers and letters
    let slug = name
      .toLowerCase()
      .trim()
      .replace(/\s+/g, '-')       // Replace spaces with hyphens
      .replace(/[^\w\-]+/g, '')   // Remove special characters
      .replace(/\-\-+/g, '-')     // Replace multiple hyphens with single hyphen
      .replace(/^-+/, '')         // Remove leading hyphens
      .replace(/-+$/, '');        // Remove trailing hyphens
    
    // Ensure slug is at least 3 characters long
    if (slug.length < 3) {
      slug = `${slug}-checkin`;
    }
    
    return slug;
  };

  // Function to check if slug exists and get a unique one if needed
  const getUniqueSlug = async (baseSlug: string): Promise<string> => {
    try {
      // First check if the base slug exists
      const { data, error } = await supabase
        .from('check_ins')
        .select('slug')
        .eq('slug', baseSlug)
        .maybeSingle();
      
      if (error) {
        console.error('Error checking slug existence:', error);
        return baseSlug; // Return base slug on error
      }
      
      // If no existing slug found, use the base slug
      if (!data) {
        console.log(`Slug '${baseSlug}' is unique, using it.`);
        return baseSlug;
      }
      
      console.log(`Slug '${baseSlug}' already exists, finding unique variation.`);
      
      // Otherwise, try adding numbers until we find a unique slug
      let counter = 1;
      let uniqueSlug = `${baseSlug}-${counter}`;
      
      while (true) {
        const { data, error } = await supabase
          .from('check_ins')
          .select('slug')
          .eq('slug', uniqueSlug)
          .maybeSingle();
        
        if (error) {
          console.error('Error checking slug uniqueness:', error);
          return uniqueSlug; // Return current attempt on error
        }
        
        if (!data) {
          console.log(`Found unique slug: '${uniqueSlug}'`);
          return uniqueSlug;
        }
        
        counter++;
        uniqueSlug = `${baseSlug}-${counter}`;
        
        // Safety check to prevent infinite loops
        if (counter > 100) {
          console.warn('Exceeded maximum slug generation attempts');
          return `${baseSlug}-${Date.now()}`;
        }
      }
    } catch (error) {
      console.error('Unexpected error during slug generation:', error);
      return `${baseSlug}-${Date.now()}`; // Fallback to timestamp suffix
    }
  };

  // Update renderCurrentStep
  const renderCurrentStep = () => {
    switch (currentStep) {
      case CheckInStep.TEMPLATE_NAME:
        return (
          <TemplateSelection
            onSelect={async (templateId) => {
              setSelectedTemplate(templateId);
            }}
            onBack={handleCancel}
            formData={formData}
            setFormData={setFormData}
            isSubmitting={isSubmitting}
            defaultSelectedTemplate={selectedTemplate}
            onContinue={async () => {
              if (!user?.id) {
                toast.error('You must be logged in to create a check-in');
                return;
              }

              if (!templateDetails) {
                toast.error('Please select a template');
                return;
              }

              setIsSubmitting(true);
              try {
                // Generate the base slug from the check-in name
                const baseSlug = generateSlug(formData.name);
                // Get a unique slug
                const slug = await getUniqueSlug(baseSlug);
                // Get user's timezone
                const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
                // Calculate due date for one-off check-in
                const dueDate = templateDetails.default_cadence === 'one-off' ? 
                  calculateNextReportDateTime(
                    templateDetails.default_cadence,
                    templateDetails.default_days_of_week,
                    templateDetails.default_scheduled_day,
                    templateDetails.default_cutoff_time,
                    userTimezone
                  ) : null;

                // Get the user's organization ID
                const { data: userData, error: userError } = await supabase
                  .from('users')
                  .select('organization_id')
                  .eq('user_id', user.id)
                  .single();

                if (userError) throw userError;
                if (!userData?.organization_id) {
                  throw new Error('User organization not found');
                }

                // Inside the onContinue handler where we process questions
                let activeQuestionIds: string[] = [];
                let processedCount = 0;
                let errorCount = 0;
                const questionIdMapping: Record<string, string> = {};

                console.log(`[CHECK-IN] Starting question processing for template ${templateDetails.template_id}`);

                try {
                  // Get template questions
                  const templateQuestionIds = await questionService.getActiveQuestionIdsForTemplate(templateDetails.template_id);
                  console.log(`[CHECK-IN] Found ${templateQuestionIds.length} questions to process`);
                  
                  // Show progress to user
                  const progressToast = toast.loading(
                    `Processing questions... (0/${templateQuestionIds.length})`, 
                    { duration: 20000 }
                  );
                  
                  // For each question, check if it needs to be duplicated
                  for (const questionId of templateQuestionIds) {
                    try {
                      console.log(`[CHECK-IN] Processing question ${questionId}`);
                      
                      const { data: question, error: questionError } = await supabase
                        .from('questions')
                        .select('question_id, organization_id, is_global')
                        .eq('question_id', questionId)
                        .single();

                      if (questionError) {
                        console.error(`[CHECK-IN] Error fetching question ${questionId}:`, questionError);
                        errorCount++;
                        toast.error(`Error processing question ${processedCount + 1}`);
                        continue;
                      }

                      if (!question) {
                        console.error(`[CHECK-IN] Question ${questionId} not found`);
                        errorCount++;
                        continue;
                      }

                      if (question.organization_id === null && question.is_global) {
                        console.log(`[CHECK-IN] Duplicating global question ${questionId}`);
                        // This is a global question, duplicate it for the organization
                        try {
                          const duplicatedQuestion = await questionService.duplicateQuestionForOrg(
                            questionId,
                            userData.organization_id,
                            user.id
                          );
                          activeQuestionIds.push(duplicatedQuestion.id);
                          questionIdMapping[questionId] = duplicatedQuestion.id;
                          console.log(`[CHECK-IN] Question mapping: ${questionId} → ${duplicatedQuestion.id}`);
                          processedCount++;
                        } catch (dupError) {
                          console.error(`[CHECK-IN] Error duplicating question ${questionId}:`, dupError);
                          errorCount++;
                          toast.error(`Failed to duplicate question ${processedCount + 1}`);
                          continue;
                        }
                      } else if (question.organization_id === userData.organization_id) {
                        console.log(`[CHECK-IN] Using existing org-specific question ${questionId}`);
                        // This is already an org-specific question
                        activeQuestionIds.push(questionId);
                        questionIdMapping[questionId] = questionId;
                        console.log(`[CHECK-IN] Question mapping: ${questionId} → ${questionId} (same ID)`);
                        processedCount++;
                      } else {
                        // This is another organization's question - skip it
                        console.warn(`[CHECK-IN] Skipping question ${questionId} as it belongs to another organization`);
                        errorCount++;
                        continue;
                      }

                      // Update progress toast
                      toast.loading(
                        `Processing questions... (${processedCount}/${templateQuestionIds.length})`,
                        { id: progressToast }
                      );

                    } catch (err) {
                      console.error(`[CHECK-IN] Unexpected error processing question ${questionId}:`, err);
                      errorCount++;
                      toast.error(`Failed to process question ${processedCount + 1}`);
                    }
                  }

                  // Clear the progress toast
                  toast.dismiss(progressToast);
                  
                  // Show final status
                  if (activeQuestionIds.length === 0) {
                    console.error('[CHECK-IN] No questions could be processed');
                    toast.error('No questions could be processed. Please try again or contact support.');
                    setIsSubmitting(false);
                    return;
                  }
                  
                  if (errorCount > 0) {
                    console.warn(`[CHECK-IN] Completed with ${errorCount} errors. Processed ${processedCount}/${templateQuestionIds.length} questions`);
                    toast.warning(
                      `Some questions could not be processed (${processedCount}/${templateQuestionIds.length} successful)`, 
                      { duration: 5000 }
                    );
                  } else {
                    console.log(`[CHECK-IN] Successfully processed all ${processedCount} questions`);
                    toast.success(`Successfully processed all questions`);
                  }

                  console.log('[CHECK-IN] Final question ID mapping:', questionIdMapping);

                  // Transform report_config with the question ID mapping
                  console.log('[CHECK-IN] Transforming report config with question ID mapping');
                  const originalReportConfig = (templateDetails as any).report_config;
                  const reportConfig = mapReportConfigQuestionIds(
                    originalReportConfig || createDefaultReportConfig(),
                    questionIdMapping
                  );

                  let newCheckInId = checkInId;
                  if (!checkInId) {
                    // Create object with check-in data to insert
                    const checkInData = {
                      name: formData.name,
                      created_by_user_id: user.id,
                      first_setup_completed: false,
                      last_completed_step: CheckInStep.TEMPLATE_NAME,
                      template_id: templateDetails.template_id,
                      slug: slug,
                      template_name: templateDetails.name,
                      cadence: templateDetails.default_cadence,
                      days_of_week: templateDetails.default_days_of_week ?? null,
                      scheduled_day: templateDetails.default_scheduled_day ?? null,
                      reminder_time: templateDetails.default_reminder_time,
                      cutoff_time: templateDetails.default_cutoff_time,
                      timezone_reminder: templateDetails.default_timezone_reminder ?? false,
                      timezone: userTimezone,
                      min_reminder_offset_hours: templateDetails.default_min_reminder_offset_hours || 1,
                      one_off_due_date: dueDate,
                      is_active: false,
                      voice_settings: templateDetails.default_voice_settings || {},
                      report_config: reportConfig || {},
                      active_question_ids: activeQuestionIds,
                      communication_channel: communicationChannel,
                      description: null,
                      slack_notification_enabled: false,
                      report_sends: null,
                      report_slack_channel_id: null,
                      report_slack_dm_user_id: null,
                    };
                  
                    console.log('Inserting check-in with data:', checkInData);
                  
                    // Insert new draft
                    const { data: inserted, error } = await supabase
                      .from('check_ins')
                      .insert(checkInData)
                      .select('check_in_id')
                      .single();
                    
                    if (error) {
                      console.error('Database error details:', error);
                      console.error('Full error object:', error);
                      toast.error(`Error creating check-in: ${error.message || 'Database error'}`);
                      throw error;
                    }
                    
                    newCheckInId = inserted.check_in_id;
                    setCheckInId(newCheckInId);
                  } else {
                    // Create object with check-in data to update
                    const checkInData = {
                      name: formData.name,
                      template_id: templateDetails.template_id,
                      last_completed_step: CheckInStep.TEMPLATE_NAME,
                      slug: slug,
                      template_name: templateDetails.name,
                      cadence: templateDetails.default_cadence,
                      days_of_week: templateDetails.default_days_of_week ?? null,
                      scheduled_day: templateDetails.default_scheduled_day ?? null,
                      reminder_time: templateDetails.default_reminder_time,
                      cutoff_time: templateDetails.default_cutoff_time,
                      timezone_reminder: templateDetails.default_timezone_reminder ?? false,
                      timezone: userTimezone,
                      min_reminder_offset_hours: templateDetails.default_min_reminder_offset_hours || 1,
                      one_off_due_date: dueDate,
                      is_active: false,
                      voice_settings: templateDetails.default_voice_settings || {},
                      report_config: reportConfig || {},
                      active_question_ids: activeQuestionIds,
                      communication_channel: communicationChannel,
                      description: null,
                      slack_notification_enabled: false,
                      report_sends: null,
                      report_slack_channel_id: null,
                      report_slack_dm_user_id: null,
                    };
                  
                    console.log('Updating check-in with data:', checkInData);
                    
                    // Update existing draft
                    const { error } = await supabase
                      .from('check_ins')
                      .update(checkInData)
                      .eq('check_in_id', checkInId);
                      
                    if (error) {
                      console.error('Database error details:', error);
                      console.error('Full error object:', error);
                      toast.error(`Error updating check-in: ${error.message || 'Database error'}`);
                      throw error;
                    }
                  }
                  setCurrentStep(CheckInStep.CHANNEL);
                } catch (err) {
                  console.error('[CHECK-IN] Fatal error handling template questions:', err);
                  toast.error('Failed to process template questions. Please try again.');
                  setIsSubmitting(false);
                  return;
                }
              } catch (error: any) {
                console.error('Full error object:', error);
                // If it's a database error, show more specific info
                if (error.code && error.details) {
                  console.error('Database error code:', error.code);
                  console.error('Database error details:', error.details);
                  console.error('Database error hint:', error.hint);
                  toast.error(`Database error: ${error.message}${error.hint ? ` (Hint: ${error.hint})` : ''}`);
                } else {
                  toast.error(error.message || 'Failed to create check-in draft');
                }
              } finally {
                setIsSubmitting(false);
              }
            }}
          />
        );
      case CheckInStep.CHANNEL:
        return (
          <Card className="w-full max-w-2xl mx-auto">
            <CardHeader>
              <CardTitle className="text-2xl">Select Communication Channel</CardTitle>
              <CardDescription>
                Choose how your team will receive check-in reminders and updates.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex gap-4">
                <button
                  className={cn(
                    'flex-1 border rounded-lg p-4 flex flex-col items-center cursor-pointer',
                    communicationChannel === 'slack' ? 'border-primary ring-2 ring-primary/10' : 'border-border hover:border-primary/50'
                  )}
                  onClick={() => {
                    setCommunicationChannel('slack');
                  }}
                  type="button"
                >
                  <img src={SlackLogo} alt="Slack" className="h-8 w-8 mb-2" />
                  <span className="font-medium">Slack</span>
                  <span className="text-xs text-muted-foreground mt-1">Send reminders via DM.</span>
                </button>
                <button
                  className={cn(
                    'flex-1 border rounded-lg p-4 flex flex-col items-center cursor-pointer',
                    communicationChannel === 'email' ? 'border-primary ring-2 ring-primary/10' : 'border-border hover:border-primary/50'
                  )}
                  onClick={() => setCommunicationChannel('email')}
                  type="button"
                >
                  <Mail className="h-8 w-8 mb-2 text-pink-500" />
                  <span className="font-medium">Email</span>
                  <span className="text-xs text-muted-foreground mt-1">Send reminders via email.</span>
                </button>
              </div>
              {communicationChannel === 'slack' && (
                <div className="mt-4">
                  <div className="flex flex-col gap-2">
                    <label className="block font-medium">Select the Slack Workspace</label>
                    <div className="flex items-center gap-3">
                      <img src={SlackLogo} alt="Slack" className="h-6 w-6" />
                      {isLoadingWorkspaces ? (
                        <span className="font-semibold text-base" style={{ color: '#4A154B' }}>
                          Loading workspaces...
                        </span>
                      ) : slackWorkspaces.length > 0 ? (
                        <div className="flex items-center gap-3 flex-1">
                          <select
                            className="flex-1 border rounded p-2"
                            value={selectedWorkspaceId || ''}
                            onChange={e => setSelectedWorkspaceId(e.target.value)}
                          >
                            {slackWorkspaces.map(workspace => (
                              <option key={workspace.workspace_id} value={workspace.workspace_id}>
                                {workspace.team_name}
                              </option>
                            ))}
                          </select>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => navigate('/settings?tab=integrations')}
                          >
                            Add Workspace
                          </Button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-3">
                          <span className="font-semibold text-base" style={{ color: '#4A154B' }}>
                            No workspace connected
                          </span>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => navigate('/settings?tab=integrations')}
                          >
                            Add Workspace
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
            <CardFooter className="flex justify-between">
              <Button
                type="button"
                variant="outline"
                onClick={handleBack}
                disabled={isSubmitting}
              >
                Back
              </Button>
              <Button
                onClick={handleChannelSelect}
                disabled={isSubmitting || (communicationChannel === 'slack' && !selectedWorkspaceId)}
              >
                Finish
              </Button>
            </CardFooter>
          </Card>
        );
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6 sm:space-y-8">
      <div className="max-w-4xl mx-auto">
        <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-3 sm:gap-4">
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold">Create a new check-in</h1>
          {checkInId && (
            <Button variant="outline" onClick={handleDiscardDraft} disabled={isSubmitting}>
              Discard Draft
            </Button>
          )}
        </div>
        <div className="mt-4">
          <CheckInProgressBar 
            currentStep={getCurrentStepNumber()} 
            totalSteps={2} 
          />
        </div>
        <div className="mt-6">{draftLoaded && renderCurrentStep()}</div>
      </div>
    </div>
  );
}